import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, Check } from 'lucide-react';
import { MacWindow } from './MacWindow';
import { formatDateValue, parseDateFlexible, getLocalIsoDateString } from '../../utils/shapeAndFormatUtils';

interface DatePickerModalProps {
  initialValue?: string;
  dateFormat?: string;
  onSelectDate: (formattedStr: string, rawIsoStr: string, formatPattern?: string) => void;
  onClose: () => void;
}

export const DatePickerModal: React.FC<DatePickerModalProps> = ({
  initialValue,
  dateFormat = 'DD/MM/YYYY',
  onSelectDate,
  onClose
}) => {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Try parsing initial value cleanly
  const parseInitialDate = () => {
    if (!initialValue) return today;
    const d = parseDateFlexible(initialValue);
    return d || today;
  };

  const [selectedDate, setSelectedDate] = useState<Date>(parseInitialDate());
  const [viewYear, setViewYear] = useState<number>(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(selectedDate.getMonth()); // 0-indexed
  const [selectedFormat, setSelectedFormat] = useState<string>(dateFormat);

  // Generate 200 Year Range (100 years past, 100 years future)
  const minYear = currentYear - 100;
  const maxYear = currentYear + 100;
  const yearsList = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Days in viewMonth
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay();

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(v => Math.max(minYear, v - 1));
    } else {
      setViewMonth(v => v - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(v => Math.min(maxYear, v + 1));
    } else {
      setViewMonth(v => v + 1);
    }
  };

  const handleDateClick = (dayNumber: number) => {
    const newD = new Date(viewYear, viewMonth, dayNumber);
    setSelectedDate(newD);
    const isoStr = getLocalIsoDateString(newD);
    const formatted = formatDateValue(isoStr, selectedFormat);
    onSelectDate(formatted, isoStr, selectedFormat);
  };

  const handleJumpToToday = () => {
    const now = new Date();
    setSelectedDate(now);
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    const isoStr = getLocalIsoDateString(now);
    const formatted = formatDateValue(isoStr, selectedFormat);
    onSelectDate(formatted, isoStr, selectedFormat);
  };

  const isSelectedDay = (day: number) => {
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getFullYear() === viewYear
    );
  };

  const isTodayDay = (day: number) => {
    return (
      today.getDate() === day &&
      today.getMonth() === viewMonth &&
      today.getFullYear() === viewYear
    );
  };

  return (
    <MacWindow isOpen={true} onClose={onClose} title="Interactive Calendar" maxWidth="max-w-md">
      <div className="flex flex-col w-full h-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-indigo-700 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-zinc-900/20 rounded-md backdrop-blur-md">
              <CalendarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-base leading-tight">Interactive Calendar</h3>
              <p className="text-xs text-blue-100 opacity-90">±100 Years Range & Live Formats</p>
            </div>
          </div>
          
        </div>

        {/* Date Format & Controls Bar */}
        <div className="p-4 bg-zinc-950 border-b border-zinc-800 space-y-3">
          <div>
            <label className="text-xs font-semibold text-zinc-400 mb-1 block">
              Date Display Format
            </label>
            <select
              value={selectedFormat}
              onChange={e => {
                const fmt = e.target.value;
                setSelectedFormat(fmt);
                const isoStr = getLocalIsoDateString(selectedDate);
                const formatted = formatDateValue(isoStr, fmt);
                onSelectDate(formatted, isoStr, fmt);
              }}
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-md text-xs font-medium text-zinc-200 focus:ring-2 focus:ring-blue-600 outline-none shadow-2xs"
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 26/08/2026)</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 08/26/2026)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-08-26)</option>
              <option value="DD MMM YYYY">DD MMM YYYY (e.g. 26 Aug 2026)</option>
              <option value="MMM DD, YYYY">MMM DD, YYYY (e.g. Aug 26, 2026)</option>
              <option value="DD MMMM YYYY">DD MMMM YYYY (e.g. 26 August 2026)</option>
              <option value="YYYY/MM/DD">YYYY/MM/DD (e.g. 2026/08/26)</option>
            </select>
          </div>

          {/* Month & Year Selectors with 100 years future & past */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              onClick={handlePrevMonth}
              className="p-1.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 rounded-md shadow-2xs transition-colors cursor-pointer"
              title="Previous Month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 flex-1 justify-center">
              <select
                value={viewMonth}
                onChange={e => setViewMonth(Number(e.target.value))}
                className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs font-semibold text-zinc-200 outline-none cursor-pointer"
              >
                {monthNames.map((name, idx) => (
                  <option key={name} value={idx}>{name}</option>
                ))}
              </select>

              <select
                value={viewYear}
                onChange={e => setViewYear(Number(e.target.value))}
                className="px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs font-bold text-zinc-200 font-mono outline-none cursor-pointer"
              >
                {yearsList.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <button
              onClick={handleNextMonth}
              className="p-1.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 rounded-md shadow-2xs transition-colors cursor-pointer"
              title="Next Month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Calendar Day Grid */}
        <div className="p-4 bg-zinc-900">
          <div className="grid grid-cols-7 gap-1 text-center mb-2">
            {daysOfWeek.map(day => (
              <span key={day} className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider py-1">
                {day}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {/* Blank offset tiles for first day */}
            {Array.from({ length: firstDayIndex }).map((_, i) => (
              <div key={`blank-${i}`} className="h-9" />
            ))}

            {/* Day tiles */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNum = i + 1;
              const selected = isSelectedDay(dayNum);
              const isToday = isTodayDay(dayNum);

              return (
                <button
                  key={dayNum}
                  onClick={() => handleDateClick(dayNum)}
                  className={`h-9 w-full rounded-md text-xs font-semibold flex items-center justify-center transition-all cursor-pointer relative ${
                    selected
                      ? 'bg-[#1473E6] text-white font-bold shadow-md scale-105 ring-2 ring-orange-300'
                      : isToday
                      ? 'bg-blue-50 text-blue-700 font-bold border border-orange-300'
                      : 'hover:bg-zinc-800 text-zinc-300'
                  }`}
                >
                  {dayNum}
                  {isToday && !selected && (
                    <span className="absolute bottom-1 w-1 h-1 rounded-full bg-[#1473E6]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Date Summary & Footer */}
        <div className="p-4 bg-zinc-950 border-t border-zinc-800 flex items-center justify-between">
          <div>
            <span className="text-[10px] text-zinc-500 font-medium block">Selected Date Value:</span>
            <span className="text-xs font-bold text-blue-700 font-mono">
              {formatDateValue(getLocalIsoDateString(selectedDate), selectedFormat)}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleJumpToToday}
              className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-300 text-zinc-300 text-xs font-semibold rounded-md transition-colors cursor-pointer"
            >
              Today
            </button>
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-[#1473E6] hover:bg-blue-700 text-white text-xs font-bold rounded-md shadow-xs flex items-center gap-1 transition-colors cursor-pointer"
            >
              <Check className="w-3.5 h-3.5" /> Apply
            </button>
          </div>
        </div>
      </div>
    </MacWindow>
  );
};

